import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
const GROUP_LABELS = {
    property: 'Properties',
    city: 'Cities',
    region: 'Regions'
};
// Ordered so matches read Properties → Cities → Regions, matching the old
// grouped dropdown.
const GROUP_ORDER = [
    'property',
    'city',
    'region'
];
const PANEL_CLASS = 'absolute z-40 mt-1 max-h-72 w-full overflow-auto rounded-lg ' +
    'border border-base-300 bg-base-100 py-1 shadow-lg';
const byLabel = (a, b) => a.label.localeCompare(b.label);
const matches = (options, query) => {
    const trimmed = query.trim().toLowerCase();
    const pool = trimmed
        ? options.filter(option => option.label.toLowerCase().includes(trimmed))
        : options;
    // Grouped Properties → Cities → Regions, alphabetical within each group,
    // flattened for keyboard navigation.
    return GROUP_ORDER.flatMap(type => [...pool.filter(option => option.type === type)].sort(byLabel));
};
// A typeahead for the property/city/region selection. Filters options as the
// salesperson types and reports the chosen option's encoded value up. The
// parent owns the selected value; this holds only the query text and open
// state.
export const SelectionTypeahead = ({ options, value, onChange, className = '' }) => {
    const selected = options.find(option => option.value === value);
    const [query, setQuery] = useState(selected?.label ?? '');
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const blurTimer = useRef();
    const results = useMemo(() => matches(options, query), [options, query]);
    const commit = (option) => {
        onChange(option.value);
        setQuery(option.label);
        setIsOpen(false);
    };
    const handleType = (next) => {
        setQuery(next);
        setActiveIndex(0);
        setIsOpen(true);
        // Editing the text invalidates any prior selection until a new one is made.
        if (value) {
            onChange('');
        }
    };
    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex(index => Math.min(index + 1, results.length - 1));
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        }
        else if (event.key === 'Enter') {
            const option = results[activeIndex];
            if (isOpen && option) {
                event.preventDefault();
                commit(option);
            }
        }
        else if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };
    const listboxId = 'selection-typeahead-list';
    let flatIndex = -1;
    return (_jsxs("div", { className: `relative ${className}`, onBlur: () => {
            // Delay so an option click (which blurs the input) can register first.
            blurTimer.current = setTimeout(() => setIsOpen(false), 100);
        }, onFocus: () => {
            if (blurTimer.current) {
                clearTimeout(blurTimer.current);
            }
        }, children: [_jsxs("div", { className: "flex h-12 items-center gap-2 rounded-lg border border-base-300 bg-base-100 px-3 lg:h-10 lg:px-2", children: [_jsx(FontAwesomeIcon, { icon: faLocationDot, className: "text-cabana" }), _jsx("input", { type: "text", role: "combobox", "aria-expanded": isOpen, "aria-controls": listboxId, "aria-autocomplete": "list", "aria-label": "Property, city, or region", placeholder: "Property, city, or region", value: query, onChange: e => handleType(e.target.value), onFocus: () => setIsOpen(true), onKeyDown: handleKeyDown, className: "w-full bg-transparent text-sm outline-none" })] }), isOpen && results.length > 0 && (_jsx("ul", { id: listboxId, role: "listbox", className: PANEL_CLASS, children: GROUP_ORDER.map(type => {
                    const groupOptions = results.filter(option => option.type === type);
                    if (groupOptions.length === 0) {
                        return null;
                    }
                    return (_jsxs("li", { children: [_jsx("div", { className: "px-3 py-1 text-xs font-semibold uppercase tracking-wide text-light-midnight", children: GROUP_LABELS[type] }), _jsx("ul", { children: groupOptions.map(option => {
                                    flatIndex += 1;
                                    const isActive = flatIndex === activeIndex;
                                    return (_jsx("li", { children: _jsx("button", { type: "button", role: "option", "aria-selected": option.value === value, 
                                            // Prevent the input blur from firing before onClick.
                                            onMouseDown: e => e.preventDefault(), onClick: () => commit(option), className: `block w-full px-3 py-2 text-left text-sm ${isActive
                                                ? 'bg-pool text-midnight'
                                                : 'text-midnight hover:bg-linen'}`, children: option.label }) }, option.value));
                                }) })] }, type));
                }) }))] }));
};
